/**
 * Standard properties attached to every PostHog event from Hire Go.
 *
 * Mirrors monolith's `Instrumentation::MetadataBuilder` shape exactly:
 *   { account_id, aws_region, tenant, occurred_at }
 *
 * `aws_region` is a monolith misnomer — the value is actually the Kubernetes
 * tenant name (see the Hire Tenants Notion db). Monolith carries both
 * `aws_region` and `tenant` set to the same value; we match that for
 * cross-repo query parity in PostHog.
 *
 * The `NEXT_PUBLIC_WAVE_ENVIRONMENT` env var is baked in at image build from
 * the `WAVE_ENVIRONMENT` build arg `.rwx/hire-go-build-image.yml` sets per
 * branch, and is the same value Sentry reports as its environment.
 *
 * `user_id` is NOT a standard property in monolith's sense — it's merged
 * in by track_event.rb only when a user_ref is present. We mirror that in
 * `track.ts`, not here.
 */

export type PostHogEnvironment = 'production' | 'staging' | 'development';

export type PostHogStandardProperties = {
  account_id: string;
  app: 'hire-go';
  aws_region: string;
  environment: PostHogEnvironment;
  is_internal_user: boolean;
  occurred_at: string;
  surface: 'frontend' | 'backend';
  tenant: string;
};

/**
 * PostHog's environment vocabulary, not the tenant name. Every non-production,
 * non-staging boot is `development` — a laptop and a preview deploy are both
 * traffic nobody wants in a product number, and without this property they were
 * indistinguishable from a customer's.
 */
export function resolveEnvironment(): PostHogEnvironment {
  const tenant = process.env.NEXT_PUBLIC_WAVE_ENVIRONMENT;
  if (tenant === 'production') {
    return 'production';
  }
  if (tenant?.startsWith('staging') || tenant === 'faut') {
    return 'staging';
  }
  return 'development';
}

export function buildStandardProperties(args: {
  accountExternalId: string;
  isInternalUser?: boolean;
  surface?: 'frontend' | 'backend';
}): PostHogStandardProperties {
  const tenant = process.env.NEXT_PUBLIC_WAVE_ENVIRONMENT ?? 'unknown';
  return {
    account_id: args.accountExternalId,
    app: 'hire-go',
    aws_region: tenant,
    environment: resolveEnvironment(),
    is_internal_user: args.isInternalUser ?? false,
    occurred_at: new Date().toISOString(),
    surface: args.surface ?? 'backend',
    tenant,
  };
}
